import {createContext, useContext, useState, useLayoutEffect, useEffect} from 'react';
import {Skeleton} from '@baidu/qianfan-antd-kit';
import {useLocation, Location, useNavigate} from 'react-router';
import {set} from 'lodash';
import {QualifyInfo} from '@/types/api/qualify';
import {SCIndexContainer} from '@/styles/modules';
import client from '@/api/client';
import {qualifyInfo, redirect} from './redirect/redirect';
import {redirectChange} from './redirect/change';

// 无需鉴权的页面
function getEscapeRoute(pathname: string) {
    const actionName = pathname;
    const ESCAPE_KEY = [
        '/confirm', '/agreement',
        '/face/result', '/auth/iframe',
        'sdk', '/education',
        '/company/noBusiness/change',
    ];
    return ESCAPE_KEY.some(key => actionName.includes(key));
}

export function requiredAuthUrl(location: any) {
    const unAuthPathName = ['iam/qualify-h5.html', 'qualify/h5/'].some(cur => location.pathname.includes(cur));
    return !getEscapeRoute(location.pathname) && !unAuthPathName;
}

async function getAfterAction(info: QualifyInfo, location: Location) {
    const actionName = location.pathname.split('/qualify/').length > 1
        ? location.pathname.split('/qualify/')[1] : '';
    const needAuth = requiredAuthUrl(location);
    if (needAuth) {
        const params = location.search.split('?').length > 1 ? location.search.split('?')[1] : '';
        const res = await client.compChangeCheck();
        if (res?.disallowedChangeScenarios?.includes('原主体已注销或吊销')) {
            set(info, 'changeInfo.isNoBusiness', true);
        }
        const changeAfter = redirectChange(info, actionName, params);
        if (changeAfter) {
            return `/qualify/${changeAfter}`;
        }
        const infoAfter = redirect(info, actionName);
        if (infoAfter) {
            return `/qualify/${infoAfter}${location.search}`;
        }
    }
    return '';
}

export const InfoContext = createContext<QualifyInfo | undefined>(undefined);

export function useQualifyInfo() {
    const info = useContext(InfoContext);
    return info;
}

export const AuthGuard = (props: any) => {
    const [info, setInfo] = useState<QualifyInfo>();
    const location = useLocation();
    const navigate = useNavigate();

    useLayoutEffect(
        () => {
            qualifyInfo().then(async info => {
                const afterAction = info ? await getAfterAction(info, location) : '';
                setInfo(info);
                if (afterAction) {
                    return navigate(afterAction, {
                        replace: true,
                        state: {
                            redirect: true,
                        },
                    });
                }
            }, () => {
                setInfo(undefined);
            });
        },
        [location.pathname]
    );

    useEffect(() => {
        if (window.top === window.self) {
            return;
        }
        // iframe内部监听内容高度变化并发送消息
        let lastHeight = 0;
        let resizeObserver = null;
        let mutationObserver = null;
        let timer: number | null = null;

        const sendHeight = () => {
            // 优先获取.ant-app的高度
            const antApp = document.querySelector('.ant-app');
            const targetElement = antApp || document.body;

            // 获取目标元素的scrollHeight
            const scrollHeight = targetElement.scrollHeight;

            // 获取目标元素的getBoundingClientRect高度
            const rect = targetElement.getBoundingClientRect();

            // 获取目标元素的computed样式，计算margin
            const style = window.getComputedStyle(targetElement);
            const marginTop = parseFloat(style.marginTop) || 0;
            const marginBottom = parseFloat(style.marginBottom) || 0;

            // 计算实际高度
            const height = Math.max(
                scrollHeight + marginTop + marginBottom,
                rect.height + marginTop + marginBottom
            );

            // 总是发送高度消息，确保高度减少时也能更新
            if (height !== lastHeight) {
                lastHeight = height;
                window.parent.postMessage({
                    type: 'QUALIFY_HEIGHT',
                    height: height,
                }, '*');
            }
        };

        const scheduleSendHeight = () => {
            if (timer) {
                window.clearTimeout(timer);
            }
            timer = window.setTimeout(sendHeight, 100);
        };

        // 初始化ResizeObserver监听.ant-app或body高度变化
        if (window.ResizeObserver) {
            const targetElement = document.querySelector('.ant-app') || document.body;
            resizeObserver = new ResizeObserver(scheduleSendHeight);
            resizeObserver.observe(targetElement);
        }

        // 初始发送高度
        sendHeight();
        scheduleSendHeight();

        // 监听DOM变化（包括子元素的增删）
        mutationObserver = new MutationObserver(scheduleSendHeight);
        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            characterData: true,
        });

        // 监听hash变化
        window.addEventListener('hashchange', scheduleSendHeight);

        return () => {
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
            window.removeEventListener('hashchange', scheduleSendHeight);
            if (timer) {
                window.clearTimeout(timer);
            }
        };
    }, [location.pathname, location.search, location.hash]);

    return (
        <InfoContext.Provider value={info}>
            {info ? props.children : <SCIndexContainer><Skeleton active /></SCIndexContainer>}
        </InfoContext.Provider>
    );
};
